/*
 * Commercial Approval — iter328 rewrite.
 *
 * Replaces the pre-iter325 5-stage workflow (GTM Sales → GTM Leader → BFR
 * → Executive → Final) with the correct 3-stage workflow:
 *
 *     GTM SALES  →  GTM LEADER  →  ADMIN
 *
 * State machine + endpoints live in /app/backend/routes/commercial_approval.py:
 *     GET  /api/leads/{id}/commercial-approval
 *     POST /api/leads/{id}/commercial-approval/start                 (gtm_sales, admin)
 *     POST /api/leads/{id}/commercial-approval/gtm-leader-approve    (gtm_sales_leader, admin)
 *     POST /api/leads/{id}/commercial-approval/admin-approve         (admin)
 *     POST /api/leads/{id}/commercial-approval/reject                (gtm_sales_leader, admin)
 *
 * CX + CX Leader NEVER see this panel — CX permissions are entirely
 * independent from the Commercial Approval state (see iter325 permission
 * refactor). Post-`admin_approved`, only GTM Sales + GTM Leader lose the
 * ability to edit the `commercials` section; every other CX/Account edit
 * flow remains unaffected.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle, Clock, Loader2, AlertTriangle, X, History, User, Circle, AlertCircle, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Canonical iter325 states. Any legacy status is normalised into one of
// these below so old accounts continue to render correctly.
const STATUS_META = {
  not_started:         { label: 'Not Started',        color: 'bg-gray-100 text-gray-600',       step: 0 },
  pending_gtm_leader:  { label: 'Pending GTM Leader', color: 'bg-amber-100 text-amber-700',     step: 1 },
  pending_admin:       { label: 'Pending Admin',      color: 'bg-blue-100 text-blue-700',       step: 2 },
  admin_approved:      { label: 'Admin Approved',     color: 'bg-emerald-100 text-emerald-700', step: 3 },
  rejected:            { label: 'Rejected',           color: 'bg-red-100 text-red-700',         step: -1 },
};

// Map any legacy status onto the 3-stage model. Kept intentionally
// conservative so no historical account looks broken.
const _LEGACY_STATUS_MAP = {
  none: 'not_started',
  gtm_sales_pending: 'pending_gtm_leader',
  commercial_pending: 'pending_gtm_leader',
  leader_approved: 'pending_admin',
  bfr_approved: 'pending_admin',
  pending_final_approval: 'pending_admin',
  final_approved: 'admin_approved',
  verified: 'admin_approved',
  completed: 'admin_approved',
};
const _normStatus = (s) => {
  const k = String(s || 'not_started').toLowerCase();
  return STATUS_META[k] ? k : (_LEGACY_STATUS_MAP[k] || 'not_started');
};

const STEPS = [
  { key: 'gtm_sales',   label: 'GTM Sales' },
  { key: 'gtm_leader',  label: 'GTM Leader' },
  { key: 'admin',       label: 'Admin' },
];

const _fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Compute TAT between two ISO timestamps → human string.
const _tat = (fromIso, toIso) => {
  if (!fromIso || !toIso) return '—';
  const ms = Math.max(0, new Date(toIso) - new Date(fromIso));
  const hrs = Math.floor(ms / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  if (hrs === 0) return `${mins}m`;
  if (hrs < 24) return `${hrs}h ${mins}m`;
  const days = Math.floor(hrs / 24);
  return `${days}d ${hrs % 24}h`;
};

const ProgressBar = ({ status }) => {
  const s = _normStatus(status);
  const currentStep = STATUS_META[s].step;
  const isRejected = s === 'rejected';
  return (
    <div className="flex items-center gap-1 w-full" data-testid="approval-progress">
      {STEPS.map((step, i) => {
        const done = !isRejected && currentStep > i + 1;
        const active = !isRejected && currentStep === i + 1;
        const isFinal = !isRejected && s === 'admin_approved' && i === STEPS.length - 1;
        return (
          <React.Fragment key={step.key}>
            <div className="flex flex-col items-center flex-1 min-w-0">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all
                ${done || isFinal ? 'bg-emerald-500 text-white'
                : active ? 'bg-amber-500 text-white ring-4 ring-amber-100'
                : isRejected ? 'bg-red-100 text-red-500' : 'bg-gray-200 text-gray-400'}`} data-testid={`approval-step-${step.key}`}>
                {done || isFinal ? <CheckCircle size={14}/> : active ? <AlertCircle size={14}/> : <Circle size={14}/>}
              </div>
              <span className={`text-[10px] mt-1 font-medium text-center truncate ${active ? 'text-amber-700' : done || isFinal ? 'text-emerald-700' : 'text-gray-500'}`}>{step.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`h-0.5 flex-1 mb-4 ${done || isFinal ? 'bg-emerald-500' : 'bg-gray-200'}`}/>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const CommercialApproval = ({ lead, onLeadUpdated }) => {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState('');
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectComment, setRejectComment] = useState('');

  // iter325 rule: CX + CX Leader never see this panel. Their edit access
  // is entirely governed by leads.edit permission and never affected by
  // Commercial Approval state.
  const role = (user?.role || '').toLowerCase();
  const HIDDEN_ROLES = new Set(['cx', 'cx_leader']);
  const isHidden = HIDDEN_ROLES.has(role);

  const authHeader = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/leads/${lead.id}/commercial-approval`, { headers: authHeader() });
      if (r.ok) setState(await r.json());
    } catch (_) {}
    setLoading(false);
  }, [lead.id]);

  useEffect(() => { if (!isHidden) load(); }, [isHidden, load]);
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(t);
  }, [error]);

  const _post = async (path, body) => {
    setActionLoading(true);
    setError('');
    try {
      const r = await fetch(`${API_BASE}/api/leads/${lead.id}/commercial-approval/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(body || {}),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      await load();
      if (onLeadUpdated) onLeadUpdated();
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (isHidden) return <div data-testid="commercial-approval-hidden" style={{ display: 'none' }} />;
  if (loading) return (
    <div className="p-4 border border-gray-200 rounded-xl bg-white flex items-center gap-2 text-sm text-gray-500">
      <Loader2 className="animate-spin" size={14}/> Loading Commercial Approval…
    </div>
  );
  if (!state) return null;

  const status = _normStatus(state.status);
  const meta = STATUS_META[status];
  const history = Array.isArray(state.history) ? state.history : [];

  const startedAt = state.started_at;
  const gtmLeaderAt = state.gtm_leader_approved_at;
  const adminAt = state.admin_approved_at;

  return (
    <div className="p-5 border border-gray-200 rounded-xl bg-white space-y-4" data-testid="commercial-approval-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-bold text-gray-900 text-base">Commercial Approval</h3>
          <p className="text-xs text-gray-500 mt-0.5">3-stage workflow: GTM Sales → GTM Leader → Admin</p>
        </div>
        <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium ${meta.color}`} data-testid="commercial-approval-status">
          {status === 'rejected' ? <AlertTriangle size={12}/> : status === 'admin_approved' ? <CheckCircle size={12}/> : <Clock size={12}/>}
          {meta.label}
        </span>
      </div>

      {/* Progress */}
      <ProgressBar status={status}/>

      {/* TAT summary — only the 2 inter-stage transitions defined by the 3-stage model. */}
      {(gtmLeaderAt || adminAt) && (
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-500 border-t border-gray-100 pt-3" data-testid="commercial-approval-tat">
          <span>GTM Sales → GTM Leader: <b className="text-gray-700">{_tat(startedAt, gtmLeaderAt)}</b></span>
          <span className="text-gray-300">|</span>
          <span>GTM Leader → Admin: <b className="text-gray-700">{_tat(gtmLeaderAt, adminAt)}</b></span>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="text-xs px-3 py-2 bg-red-50 text-red-700 border border-red-200 rounded" data-testid="commercial-approval-error">{error}</div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2">
        {state.can_start && (
          <button onClick={() => _post('start', {})} disabled={actionLoading}
            className="px-3 py-1.5 bg-teal-600 text-white text-xs rounded-lg hover:bg-teal-700 disabled:opacity-60 flex items-center gap-1"
            data-testid="commercial-approval-start">
            {actionLoading ? <Loader2 className="animate-spin" size={12}/> : <ArrowRight size={12}/>}
            Start Approval
          </button>
        )}
        {state.can_gtm_leader_approve && (
          <button onClick={() => _post('gtm-leader-approve', {})} disabled={actionLoading}
            className="px-3 py-1.5 bg-emerald-600 text-white text-xs rounded-lg hover:bg-emerald-700 disabled:opacity-60 flex items-center gap-1"
            data-testid="commercial-approval-gtm-leader-approve">
            {actionLoading ? <Loader2 className="animate-spin" size={12}/> : <CheckCircle size={12}/>}
            Approve as GTM Leader
          </button>
        )}
        {state.can_admin_approve && (
          <button onClick={() => _post('admin-approve', {})} disabled={actionLoading}
            className="px-3 py-1.5 bg-emerald-600 text-white text-xs rounded-lg hover:bg-emerald-700 disabled:opacity-60 flex items-center gap-1"
            data-testid="commercial-approval-admin-approve">
            {actionLoading ? <Loader2 className="animate-spin" size={12}/> : <CheckCircle size={12}/>}
            Approve as Admin
          </button>
        )}
        {state.can_reject && (
          <button onClick={() => setRejectModal(true)} disabled={actionLoading}
            className="px-3 py-1.5 bg-red-50 text-red-700 text-xs rounded-lg hover:bg-red-100 border border-red-200 flex items-center gap-1"
            data-testid="commercial-approval-reject">
            <X size={12}/> Reject
          </button>
        )}
        <button onClick={() => setShowHistory(s => !s)} className="ml-auto px-2 py-1 text-xs text-gray-500 hover:text-teal-600 flex items-center gap-1" data-testid="commercial-approval-history-toggle">
          <History size={12}/> {showHistory ? 'Hide' : 'View'} History ({history.length})
        </button>
      </div>

      {/* History */}
      {showHistory && (
        <div className="border-t border-gray-100 pt-3 space-y-1.5" data-testid="commercial-approval-history">
          {history.length === 0 && <p className="text-xs text-gray-400">No history yet.</p>}
          {history.map((h, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-gray-600">
              <User size={11} className="mt-0.5 text-gray-400"/>
              <div className="flex-1 min-w-0">
                <span className="font-medium text-gray-800">{h.actor_name || h.actor_id || 'System'}</span>
                <span className="text-gray-400"> ({h.actor_role})</span>
                <span> — {h.action}</span>
                {h.comment && <span className="italic text-gray-500"> · {h.comment}</span>}
              </div>
              <span className="text-[10px] text-gray-400 shrink-0">{_fmtTime(h.at)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Reject modal */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setRejectModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="commercial-approval-reject-modal">
            <h3 className="font-bold text-gray-900 text-sm mb-2">Reject Commercial Approval</h3>
            <p className="text-xs text-gray-500 mb-3">Provide a reason so the requester knows what to fix.</p>
            <textarea value={rejectComment} onChange={(e) => setRejectComment(e.target.value)}
              rows={3} maxLength={500} className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="Reason for rejection…" data-testid="commercial-approval-reject-input"/>
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => setRejectModal(false)} className="px-3 py-1.5 text-xs text-gray-600">Cancel</button>
              <button
                onClick={async () => { await _post('reject', { comment: rejectComment }); setRejectModal(false); setRejectComment(''); }}
                disabled={actionLoading}
                className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 disabled:opacity-60"
                data-testid="commercial-approval-reject-confirm">
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommercialApproval;
